import { SourceConfig, CandidatePayload } from '../types';
import { buildRelevanceFields } from '../relevance';
import {
  compressCityIds as hhCompressCityIds,
  getAreaNameById as hhGetAreaNameById,
} from '../../services/hhDictionariesService';

/**
 * FR-3.2 + FR-3.3: Конфигурация и маппер для источника E-Staff (через HR-Proxy).
 * Также используется для SKILLAZ (тот же HR-Proxy, другой source param).
 */
export const estaffConfig: SourceConfig = {
  id: 'estaff',
  label: 'E-Staff',
  aiRecruiterSource: 'ESTAFF',
  enabledMessengers: ['WHATSAPP', 'TELEGRAM', 'MAX'],
  messengerPriority: ['WHATSAPP', 'TELEGRAM', 'MAX'],
  filterSections: ['company', 'additional', 'dates', 'skills', 'companies', 'includeNoAge'],
  // Estaff/Skillaz исторически использовали те же hh-функции для пост-
  // обработки выбранных city id'ов (компонент AreaModal всегда зовёт
  // compressCityIds / getAreaNameById на onApply). Сохраняем поведение
  // как было до перевода на postProcessors-архитектуру.
  postProcessors: {
    compressCityIds: hhCompressCityIds,
    getAreaNameById: hhGetAreaNameById,
  },

  toPayload(candidate: Record<string, unknown>): CandidatePayload {
    const phone = normalizePhone(candidate.phone as string);
    const telegram = (candidate.telegram as string) || phone || null;
    const whatsapp = normalizePhone(candidate.whatsapp as string) || phone;

    return {
      externalId: String(candidate.id),
      source: 'ESTAFF',
      sourceExternalId: String(candidate.id),
      messengerContacts: {
        telegram,
        whatsapp,
        phone,
        max: (candidate.max as string) || null,
        hhResumeId: null,
      },
      name: (candidate.fullName as string) || (candidate.name as string) || '',
      firstName: pickNamePart(candidate, 'firstName', 'first_name'),
      lastName: pickNamePart(candidate, 'lastName', 'last_name'),
      middleName: pickNamePart(candidate, 'middleName', 'middle_name'),
      photoUrl: (candidate.photo as string) || null,
      resumeUrl: (candidate.resumeUrl as string) || null,
      resumeBrief: buildBrief(candidate),
      metadata: {
        experience: candidate.experience || '',
        skills: candidate.skills || [],
        resumeUrl: (candidate.resumeUrl as string) || null,
        photoUrl: (candidate.photo as string) || null,
        lastUpdated: (candidate.updated as string) || new Date().toISOString(),
        source: 'estaff',
      },
      ...buildRelevanceFields(candidate),
    };
  },
};

/** Конфигурация Skillaz — идентична E-Staff, другой source */
export const skillazConfig: SourceConfig = {
  ...estaffConfig,
  id: 'skillaz',
  label: 'Skillaz',
  aiRecruiterSource: 'SKILLAZ',

  toPayload(candidate: Record<string, unknown>): CandidatePayload {
    const payload = estaffConfig.toPayload(candidate);
    payload.source = 'SKILLAZ';
    payload.metadata.source = 'skillaz';
    return payload;
  },
};

function normalizePhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  return phone.replace(/[\s()\-+]/g, '').replace(/^8/, '7');
}

function pickNamePart(candidate: Record<string, unknown>, camelName: string, snakeName: string): string | null {
  return normalizeString(candidate[camelName]) || normalizeString(candidate[snakeName]);
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function buildBrief(candidate: Record<string, unknown>): string {
  const parts: string[] = [];
  if (candidate.experience) {
    const match = String(candidate.experience).match(/(\d+)/);
    if (match) {
      const months = parseInt(match[1]);
      const years = Math.floor(months / 12);
      parts.push(years > 0 ? `${years}+ лет опыта` : `${months} мес. опыта`);
    }
  }
  if (candidate.salary) {
    const rawCur = candidate.salaryCurrency as any;
    const cur: string = (typeof rawCur === 'object' && rawCur !== null ? String(rawCur.code || '') : String(rawCur || '')) || 'RUB';
    parts.push(`Ожидаемая ЗП: ${candidate.salary} ${cur}`);
  }
  return parts.join(' | ') || (candidate.title as string) || '';
}

export default estaffConfig;
